import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type KeyboardEvent, type ReactNode, useCallback, useId, useRef, useState } from "react";
import { ALPHA, BADGE, MOTION } from "../../../../theme/tokens";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { SystemReactTinyPopover, SystemReactTinyPopoverRootStyle } from "../../Popups/SystemReactTinyPopover";
import SystemButton from "../../SystemButton/SystemButton";

/** Default width of the picker surface — wide enough for a name + status chip without wrapping. */
const DEFAULT_PICKER_WIDTH = 320;
/** Keeps the surface inside the viewport on a phone, where the anchor sits near the screen edge. */
const PICKER_MAX_WIDTH = "calc(100vw - 48px)";

type Props = {
	/** The current record's name — already formatted/translated. Rendered as the pill's label. */
	label: string;
	/**
	 * The picker rendered inside the popover (a `StaffAutoComplete`, a facility list, …).
	 * Call `close()` once a record is picked so the switcher dismisses itself.
	 */
	renderPicker: (args: { close: () => void }) => ReactNode;
	/** Accessible name for the trigger — say what switching does ("Switch employee"). */
	ariaLabel: string;
	/** Hover text on the pill. Defaults to `ariaLabel`. */
	tooltip?: string;
	disabled?: boolean;
	/** Width of the picker surface (default 320). */
	pickerWidth?: number | string;
	/** Notified on open/close — for analytics. */
	onOpenChange?: (open: boolean) => void;
	/**
	 * Typography + layout of the label. The caller owns these so the pill can BE the page's
	 * title (its font size/weight/color), while the pill chrome stays the component's.
	 */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * Turns the name of the record a page is about into a **switcher**: a pill showing the current
 * record, which opens an anchored picker to jump straight to a sibling record — instead of making
 * the user navigate back to the list in between (APP-535).
 *
 * Generic over what is being switched: it owns the trigger affordance (tinted pill, animated caret,
 * press-scale, keyboard + `aria-haspopup` semantics, focus return on close) and the popover; the
 * caller supplies the picker via `renderPicker` and the label's typography via `sx`. Give the picker
 * its own `autoFocus` so the switcher is type-to-search straight from the click.
 */
export const RecordSwitcher = ({ label, renderPicker, ariaLabel, tooltip, disabled, pickerWidth, onOpenChange, sx, dataTestId }: Props) => {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	// The trigger advertises `aria-haspopup="dialog"`, so the surface it opens has to actually be one
	// and be pointed at — otherwise assistive tech is told about a popup it can neither find nor name.
	const pickerId = useId();

	const setOpenState = useCallback(
		(next: boolean) => {
			setOpen(next);
			onOpenChange?.(next);
		},
		[onOpenChange],
	);

	const close = useCallback(() => {
		setOpenState(false);
		// The picker took focus on open, so hand it back rather than dropping it on <body>.
		triggerRef.current?.focus();
	}, [setOpenState]);

	/**
	 * Land the caret in the picker's search field so the switcher is type-to-search straight from the
	 * click — and do it a frame after the picker mounts: the popover measures its content before
	 * attaching it to the document, and a `focus()` on a detached node is silently dropped (which is
	 * why the picker's own React `autoFocus` isn't enough here). Every picker gets this without opting in.
	 */
	const focusPicker = useCallback((element: HTMLDivElement | null) => {
		if (!element) {
			return;
		}
		requestAnimationFrame(() => element.querySelector<HTMLElement>("input, [tabindex]:not([tabindex='-1'])")?.focus());
	}, []);

	// Escape inside the picker closes the switcher. The picker's own overlays (an autocomplete
	// listbox) consume Escape first, so this only fires once they are closed.
	const handlePickerKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.stopPropagation();
				close();
			}
		},
		[close],
	);

	return (
		<SystemReactTinyPopover
			isOpen={open}
			onClose={close}
			positions={["bottom", "top"]}
			align="start"
			padding={6}
			content={
				<Box
					ref={focusPicker}
					id={pickerId}
					role="dialog"
					aria-label={ariaLabel}
					onKeyDown={handlePickerKeyDown}
					sx={{ width: pickerWidth ?? DEFAULT_PICKER_WIDTH, maxWidth: PICKER_MAX_WIDTH }}
					data-testid={dataTestId ? `${dataTestId}-picker` : undefined}
				>
					{renderPicker({ close })}
				</Box>
			}
			contentRootStyle={SystemReactTinyPopoverRootStyle}
		>
			{/*
			 * The hint is wrapped here rather than passed to SystemButton's `tooltip`, and emptied — not
			 * removed — while open: that prop swaps the button between a wrapped and an unwrapped tree, and
			 * remounting the trigger mid-click detaches the click's target, which the popover's outside-click
			 * check then reads as a click outside itself and closes on the spot. The Box is what SystemButton
			 * itself does for a tooltip — the button takes only declared props, so the Tooltip's hover
			 * handlers need an element that accepts them.
			 */}
			<InHouseTooltip title={open ? "" : (tooltip ?? ariaLabel)}>
				<Box sx={{ display: "inline-flex", maxWidth: "100%" }}>
					<SystemButton
						ref={triggerRef}
						buttonVariant="borderless"
						noFullWidthMobile
						disabled={disabled}
						onClick={() => setOpenState(!open)}
						aria-haspopup="dialog"
						aria-expanded={open}
						aria-controls={open ? pickerId : undefined}
						aria-label={ariaLabel}
						data-testid={dataTestId}
						sx={[
							(theme) => ({
								// The pill is chrome around the caller's typography: height/padding/color come from
								// `sx` below, so a page title keeps its own scale instead of the button's 32px shell.
								height: "auto",
								minHeight: 0,
								maxWidth: "100%",
								px: 1,
								py: 0.25,
								gap: 0.5,
								borderRadius: BADGE.PILL_RADIUS_FULL,
								fontSize: "inherit",
								fontWeight: "inherit",
								lineHeight: "inherit",
								textAlign: "start",
								backgroundColor: alpha(theme.palette.primary.main, ALPHA.HOVER_OVERLAY * 1.5),
								transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
								"&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) },
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) },
								},
								"&:active": { transform: "scale(0.97)" },
								"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
							}),
							...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
						]}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, maxWidth: "100%" }}>
							<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
								{label}
							</Box>
							<ExpandMoreIcon
								sx={{
									flexShrink: 0,
									fontSize: "0.85em",
									opacity: open ? 1 : 0.7,
									transition: `transform ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}, opacity ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
									transform: open ? "rotate(180deg)" : "none",
									"@media (prefers-reduced-motion: reduce)": { transition: "none" },
								}}
							/>
						</Box>
					</SystemButton>
				</Box>
			</InHouseTooltip>
		</SystemReactTinyPopover>
	);
};

export default RecordSwitcher;
